import React, { useContext, useEffect, useRef, useState } from "react";
import { ICustomersListingProps } from "./CustomersListing.types";
import { ActionButton, IIconProps, Selection, IObjectWithKey, Stack } from "@fluentui/react";
import { QuotationService } from "../../../services/Quotations/QuotationService";
import { CoherenceDataGrid } from "@coherence-design-system/data-grid";
import { SelectionMode } from "@fluentui/utilities";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ErrorsDefaultState,
  InitialQuotationInfo,
  NEW_ACTION,
} from "../Common/QuotationData";
import { appInsights } from "../../../services/Logging/AppInsights";
import { QuotationContext } from "../QuotationContext";
import { IQuotationType, QuotationInfoType } from "../Quotation.types";
import { useLocalStorageContext } from "../../../Provider/LocalStorageProvider";
import { UserRoles } from "../../../common/enum/UserRoles";
import { CustomerAccountInfo } from "../../CustomerAccount/Customer.types";
const AddIcon: IIconProps = { iconName: "Add" };
export const CustomersListing: React.FunctionComponent<
  ICustomersListingProps
> = (props) => {
  const { isLoading, setIsLoading } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const { setQuotationInfo } = useContext(
    QuotationContext
  ) as QuotationInfoType;
  const { setQuotationError } = useContext(QuotationContext) as IQuotationType;
  const [customerData, setCustomerData] = useState([]);
  const state = location?.state;
  const { genericRole, userRole } = useLocalStorageContext();
  const requestIdRef = useRef(0);

  const getRecentCustomers = () => {
    const currentRequestId = ++requestIdRef.current;
    setIsLoading(true);
    appInsights.trackEvent({
      name: "In CustomersListing.tsx, triggering get Recent Quote Customers API.",
    });
    QuotationService.getRecentQuoteCustomers()
      .then((res) => {
        if (currentRequestId === requestIdRef.current) {
          if (res?.data?.data) {
            appInsights.trackEvent({
              name: "In CustomersListing.tsx, recent customers list feteched successfully.",
            });
            setCustomerData(res.data.data);
          } else {
            appInsights.trackEvent({
              name: "In CustomersListing.tsx, recent customers list does not contain any data.",
            });
            customerData.length > 0 && setCustomerData([]);
          }
          setIsLoading(false);
        }
      })
      .catch((error) => {
        if (currentRequestId === requestIdRef.current) {
          appInsights.trackException({
            error,
            properties: {
              name: "In CustomersListing.tsx, error while getting all quotations in Quotation.tsx",
            },
          });
          customerData.length > 0 && setCustomerData([]);
          setIsLoading(false);
        }
      });
  };
  const getSearchedCustomerInfo = () => {
    const currentRequestId = ++requestIdRef.current;
    setIsLoading(true);
    appInsights.trackEvent({
      name: "In CustomersListing.tsx, triggering get Recent Quote Customers API.",
    });
    QuotationService.searchCustomerInfo(state.searchingKey)
      .then((res) => {
        if (currentRequestId === requestIdRef.current) {
          if (res?.data?.data) {
            appInsights.trackEvent({
              name: "In CustomersListing.tsx, recent customers list feteched successfully.",
            });
            setCustomerData(res.data.data);
            props?.setItemsCount && props?.setItemsCount(res.data.data.length);
          } else {
            appInsights.trackEvent({
              name: "In CustomersListing.tsx, recent customers list does not contain any data.",
            });
            setCustomerData([]);
            props?.setItemsCount && props.setItemsCount(0);
          }
          setIsLoading(false);
        }
      })
      .catch((error) => {
        if (currentRequestId === requestIdRef.current) {
          appInsights.trackException({
            error,
            properties: {
              name: "In CustomersListing.tsx, error while getting all quotations in Quotation.tsx",
            },
          });
          setCustomerData([]);
          props?.setItemsCount && props.setItemsCount(0);
          setIsLoading(false);
        }
      });
  };

  useEffect(() => {
    if (state?.searchingKey?.length >= 3 && location.pathname !== "/") {
      setCustomerData([]);
      getSearchedCustomerInfo();
    } else if ((customerData.length === 0) || (location.pathname !== "/")) {
      setCustomerData([]);
      getRecentCustomers();
    }
  }, [state?.searchingKey]);

  const _onClickCustomerAccount = (accountId: any) => {
    const selectedCustomer = customerData.find((c: any) => c.accountId === accountId);
    if (selectedCustomer) {
      navigate("/CustomerEditContact", {
        state: selectedCustomer,
      });
    }
  };


  const _onClickCustomerName = (accountId: any) => {
    setQuotationError({
      ...ErrorsDefaultState,
    });
    setQuotationInfo({
      ...InitialQuotationInfo,
      quoteLineItems: [],
      quoteLineItemsClone: [],
      standaloneItems: [],
      standaloneItemsClone: [],
      bundles: [],
      bundleItems: [],
      bundleItemsClone: [],
    });

    const customer = customerData.filter(
      (item: CustomerAccountInfo): item is CustomerAccountInfo => item.accountId === accountId
      );
    const customerAccountInfo:CustomerAccountInfo = customer[0];
    navigate("/AddQuotation", {
      state: {
        actionName: NEW_ACTION,
        customerId: accountId,
      },
    });
  };

  const _onClickNewQuotation = () => {
    if (selectedGridData?.length) {
      const { accountId } = selectedGridData[0] as any;
      setQuotationError({
        ...ErrorsDefaultState,
      });
      setQuotationInfo({
        ...InitialQuotationInfo,
        quoteLineItems: [],
        quoteLineItemsClone: [],
        standaloneItems: [],
        standaloneItemsClone: [],
        bundles: [],
        bundleItems: [],
        bundleItemsClone: [],
      });
      navigate("/AddQuotation", {
        state: {
          actionName: NEW_ACTION,
          customerId: accountId,
        },
      });
    }
  };

  const [selectedGridData, setSelectedGridData] = React.useState<
    IObjectWithKey[] | undefined
  >(undefined);

  const customerSelection = React.useMemo(
    () =>
      new Selection({
        onSelectionChanged: () => {
          appInsights.trackEvent({
            name: "In CustomersListing.tsx, calling selection change on Click checkbox",
            properties: { selection: customerSelection.getSelection() },
          });
          setSelectedGridData(customerSelection.getSelection());

        },
        selectionMode: SelectionMode.single,
      }),
    [JSON.stringify(customerData)]
  );


  return (
    <Stack
      style={{
        marginLeft: "30px",
        marginRight: "30px",
        marginTop: "10px",
        height: 600,
      }}
    >
      {location.pathname === "/" && customerData?.length > 0 && (
        <ActionButton iconProps={AddIcon} onClick={_onClickNewQuotation}  style={{display: "flex",justifyContent: "left"}} disabled={selectedGridData?.length != 1}>
          Create New Quotation
        </ActionButton>
      )}
      <CoherenceDataGrid
        ariaLabelForSelectAllCheckbox="select all checkbox"
        checkButtonAriaLabel="checkbox"
        compactMode="compact"
        data={customerData}
        isLoading={isLoading}
        isScrollable
        isSortable
        listConfig={[
          {
            fieldName: "accountId",
            key: "accountId",
            name: "Customer Account",
            type:
              genericRole !== -1 && userRole !== UserRoles.GTFS
                ? "link"
                : "string",
            url: "",
            linkProps: {
              onClick: (item: any) =>
                location.pathname === "/" && customerData?.length > 0 ? _onClickCustomerAccount(item.target.innerHTML) : _onClickCustomerName(item.target.innerHTML),
              style: { textDecoration: "underline" },
            },
            isResizable: true,
            maxWidth: 180,
          },
          {
            fieldName: "name",
            key: "name",
            name: "Customer Name",
            type: "string",
            isResizable: true,
            maxWidth: 200,
          },
          {
            fieldName: "authorizedBuyerFirstName",
            isResizable: true,
            key: "primaryContact",
            name: "Primary Contact",
            type: "string",
            maxWidth: 225,
          },
          {
            fieldName: "authorizedEmailId",
            isResizable: true,
            key: "authorizedEmailId",
            name: "Email",
            type: "string",
            maxWidth: 225,
          },
          {
            fieldName: "authorizedBuyerPhone",
            isResizable: true,
            key: "telephone",
            name: "Phone",
            type: "string",
            maxWidth: 225,
          },
          {
            fieldName: "country",
            isResizable: true,
            key: "country",
            name: "Country",
            type: "string",
          },
        ]}
        selection={customerSelection}
        selectionMode={location.pathname === "/" && customerData?.length > 0 ? SelectionMode.single : SelectionMode.none}
        selectionPreservedOnEmptyClick
        setKey="CUSTOMER_DATA_LIST_DATA_GRID_SORTABLE_SELECTABLE"
      />
    </Stack>
  );
};
